// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showTCLink = "showTCLink",
    tcLinkPreFixText = "tcLinkPreFixText",
    tcLink = "tcLink",
    tcLinkTextOverride = "tcLinkTextOverride",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
    fields: {
        [fields.showTCLink]: boolean;
        [fields.tcLinkPreFixText]: string;
        [fields.tcLink]: LinkFieldValue;
        [fields.tcLinkTextOverride]: string;
        [fields.openInNewTab]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface ZeroPricingDisclaimerStyles {
    wrapper?: InjectableCss;
}

const zeroPricingDisclaimerStyles: ZeroPricingDisclaimerStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(40), pxToRem(50))}
        `,
    },
};

const styles = zeroPricingDisclaimerStyles;

const ZeroPricingDisclaimer = ({
    currentPage,
    orderState: baseOrderState,
    fields: { showTCLink, tcLinkPreFixText, tcLink, tcLinkTextOverride, openInNewTab },
}: Props) => {
    const { url, title } = useGetLink(tcLink);

    const cart = useAppSelector(state => getCurrentCartState(state).value);
    const orderStateContext = useContext(OrderStateContext);
    const orderState = baseOrderState || orderStateContext;
    const order = orderState?.value;

    let hasZeroPriceItem = false;

    if (currentPage.type === "CartPage" || currentPage.type === "CheckoutReviewAndSubmitPage") {
        hasZeroPriceItem = cart?.cartLines ? cart.cartLines.some(c => c?.pricing?.unitNetPrice === 0) : false;
    }

    if (currentPage.type === "OrderDetailsPage") {
        hasZeroPriceItem = order?.orderLines ? order.orderLines.some(c => c?.totalRegularPrice === 0) : false;
    }

    if (!hasZeroPriceItem) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <InfosBox>
                <Typography>
                    {siteMessage("Multiple_ZeroPriceDisclaimer")}
                    {showTCLink && (
                        <>
                            {` ${tcLinkPreFixText} `}
                            <Link href={url} target={openInNewTab ? "_blank" : ""}>
                                {tcLinkTextOverride || title}
                            </Link>
                        </>
                    )}
                </Typography>
            </InfosBox>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ZeroPricingDisclaimer),
    definition: {
        displayName: "Zero Pricing Disclaimer",
        group: "Common",
        icon: "circle-i",
        fieldDefinitions: [
            {
                name: fields.showTCLink,
                displayName: "Display ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                variant: "toggle",
                fieldType: "General",
            },
            {
                name: fields.tcLinkPreFixText,
                displayName: "Link Prefix",
                editorTemplate: "TextField",
                defaultValue: "See",
                fieldType: "Translatable",
                isVisible: x => x.fields.showTCLink,
            },
            {
                name: fields.tcLink,
                displayName: "Terms and Conditions Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: x => false,
                allowCategories: x => false,
                isVisible: x => x.fields.showTCLink,
            },
            {
                name: fields.tcLinkTextOverride,
                displayName: "Link Title Override",
                editorTemplate: "TextField",
                defaultValue: "Terms and Conditions",
                fieldType: "Translatable",
                isVisible: x => x.fields.showTCLink,
            },
            {
                name: fields.openInNewTab,
                displayName: "Open In New Tab?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                variant: "toggle",
                fieldType: "General",
                isVisible: x => x.fields.showTCLink,
            },
        ],
        allowedContexts: ["OrderDetailsPage", "CartPage", "CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
